import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const WORLD_GEO_URL = '/world-countries-110m.json';

const COUNTRY_NAME_ALIASES = {
  'UNITED STATES OF AMERICA': 'UNITED STATES',
  'RUSSIAN FEDERATION': 'RUSSIA',
  'KOREA, REPUBLIC OF': 'SOUTH KOREA',
  'KOREA, DEMOCRATIC PEOPLE\'S REPUBLIC OF': 'NORTH KOREA',
  'VIET NAM': 'VIETNAM',
  'UNITED REPUBLIC OF TANZANIA': 'TANZANIA',
  CZECHIA: 'CZECH REPUBLIC',
  'BOLIVIA (PLURINATIONAL STATE OF)': 'BOLIVIA',
  'IRAN (ISLAMIC REPUBLIC OF)': 'IRAN',
  'VENEZUELA (BOLIVARIAN REPUBLIC OF)': 'VENEZUELA',
  'SYRIAN ARAB REPUBLIC': 'SYRIA',
  'LAO PEOPLE\'S DEMOCRATIC REPUBLIC': 'LAOS',
  'MOLDOVA, REPUBLIC OF': 'MOLDOVA',
  'BRUNEI DARUSSALAM': 'BRUNEI',
  'TAIWAN, PROVINCE OF CHINA': 'TAIWAN',
};

const normalizeCountry = (value) => {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  return COUNTRY_NAME_ALIASES[raw] || raw;
};

// Reference viewBox/scale that renders a full, well-framed world map.
const BASE_MAP_WIDTH = 900;
const BASE_MAP_HEIGHT = 450;
const BASE_MAP_SCALE = 165;

// SEC overview data mostly tags sector as generic filer-type strings, not a real sector.
const UNCATEGORIZED_SECTOR_VALUES = new Set(['', 'OPERATING', 'OTHER']);
const getSectorLabel = (sector) => {
  const trimmed = String(sector || '').trim();
  if (!trimmed || UNCATEGORIZED_SECTOR_VALUES.has(trimmed.toUpperCase())) return 'Uncategorized';
  return trimmed;
};

const formatMarketCap = (value) => {
  if (!Number.isFinite(value) || value <= 0) return 'N/A';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
};

const CATEGORY_TABS = [
  { value: 'all', label: 'By Country' },
  { value: 'top20', label: 'Top 20 (Mkt Cap)' },
  { value: 'sector', label: 'By Sector' },
  { value: 'global500', label: 'Top 500 (Mkt Cap)' },
];

const CountryTickerMap = ({ onSelectTicker, fullPage = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [hoveredCountry, setHoveredCountry] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [search, setSearch] = useState('');
  const [categoryTab, setCategoryTab] = useState('all');
  const mapContainerRef = useRef(null);
  const [mapBoxSize, setMapBoxSize] = useState({ width: BASE_MAP_WIDTH, height: BASE_MAP_HEIGHT });

  // Match the SVG viewBox to the actual container aspect ratio so the map
  // fills the whole panel instead of being letterboxed inside it.
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return undefined;

    const updateSize = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setMapBoxSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/api/tickers/by-country`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const items = Array.isArray(payload?.countries) ? payload.countries : [];

        setCountries(items.map((row) => ({
          ...row,
          countryNormalized: normalizeCountry(row.country),
          tickers: Array.isArray(row.tickers) ? row.tickers : [],
          count: Number(row.count) || 0,
        })));
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Failed to load country ticker map');
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  const countryMap = useMemo(() => {
    const map = new Map();
    countries.forEach((row) => {
      map.set(row.countryNormalized, row);
    });
    return map;
  }, [countries]);

  const maxCount = useMemo(() => {
    return countries.reduce((acc, row) => Math.max(acc, row.count || 0), 0);
  }, [countries]);

  const totalTickers = useMemo(() => {
    return countries.reduce((acc, row) => acc + (Number(row.count) || 0), 0);
  }, [countries]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return countries;
    return countries.filter((row) => {
      if (String(row.country || '').toUpperCase().includes(q)) return true;
      return row.tickers.some((tickerRow) => {
        return (
          String(tickerRow.ticker || '').toUpperCase().includes(q)
          || String(tickerRow.name || '').toUpperCase().includes(q)
        );
      });
    });
  }, [countries, search]);

  const activeCountry = selectedCountry || hoveredCountry;
  const activePayload = useMemo(() => {
    if (!activeCountry) return null;
    return countryMap.get(normalizeCountry(activeCountry)) || null;
  }, [activeCountry, countryMap]);

  const visibleCountrySet = useMemo(() => {
    return new Set(filteredCountries.map((row) => row.countryNormalized));
  }, [filteredCountries]);

  const countryTopByMarketCap = useMemo(() => {
    if (!activePayload) return [];
    return [...activePayload.tickers]
      .filter((t) => Number.isFinite(t.marketCap) && t.marketCap > 0)
      .sort((a, b) => b.marketCap - a.marketCap)
      .slice(0, 20);
  }, [activePayload]);

  const countrySectorGroups = useMemo(() => {
    if (!activePayload) return [];
    const groups = new Map();
    activePayload.tickers.forEach((t) => {
      const label = getSectorLabel(t.sector);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(t);
    });
    return [...groups.entries()]
      .map(([label, tickers]) => ({
        label,
        tickers: [...tickers].sort((a, b) => String(a.ticker).localeCompare(String(b.ticker))),
      }))
      .sort((a, b) => {
        if (a.label === 'Uncategorized') return 1;
        if (b.label === 'Uncategorized') return -1;
        return b.tickers.length - a.tickers.length;
      });
  }, [activePayload]);

  const globalTopByMarketCap = useMemo(() => {
    const flattened = [];
    countries.forEach((row) => {
      row.tickers.forEach((t) => {
        if (Number.isFinite(t.marketCap) && t.marketCap > 0) {
          flattened.push({ ...t, country: row.country });
        }
      });
    });
    return flattened.sort((a, b) => b.marketCap - a.marketCap).slice(0, 500);
  }, [countries]);

  const marketCapCoverage = useMemo(() => {
    const withCap = countries.reduce(
      (acc, row) => acc + row.tickers.filter((t) => Number.isFinite(t.marketCap) && t.marketCap > 0).length,
      0
    );
    return { withCap, total: totalTickers };
  }, [countries, totalTickers]);

  const getCountryColor = (countryName) => {
    const normalized = normalizeCountry(countryName);
    const item = countryMap.get(normalized);
    if (!item || !visibleCountrySet.has(normalized)) return '#e5e7eb';
    const ratio = maxCount > 0 ? item.count / maxCount : 0;
    if (ratio > 0.66) return '#0f766e';
    if (ratio > 0.33) return '#14b8a6';
    return '#99f6e4';
  };

  const handleTickerOpen = (tickerRow) => {
    if (!tickerRow?.ticker) return;
    if (typeof onSelectTicker === 'function') {
      onSelectTicker(tickerRow.ticker, tickerRow.exchange || 'UNKNOWN');
    }
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load country ticker map: {error}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <PublicIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Country Ticker Map</Typography>
        <Chip
          label={`${totalTickers} tickers across ${countries.length} countries`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ ml: 0.5 }}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Hover or click a country to view listed tickers. Click a ticker chip to open its dashboard.
      </Typography>

      <TextField
        size="small"
        fullWidth
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search country, ticker, or company..."
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
      />

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
        <Paper
          ref={mapContainerRef}
          variant="outlined"
          onMouseLeave={() => setHoveredCountry('')}
          sx={{
            flex: 1,
            p: 0,
            minHeight: fullPage ? 640 : 420,
            height: fullPage ? { xs: 640, lg: 'calc(100vh - 210px)' } : { xs: 420, lg: 620 },
            overflow: 'hidden',
          }}
        >
          <ComposableMap
            projectionConfig={{
              scale: Math.max(
                (mapBoxSize.width / BASE_MAP_WIDTH) * BASE_MAP_SCALE,
                (mapBoxSize.height / BASE_MAP_HEIGHT) * BASE_MAP_SCALE
              ),
            }}
            width={mapBoxSize.width}
            height={mapBoxSize.height}
            style={{ width: '100%', height: '100%' }}
          >
            <Geographies geography={WORLD_GEO_URL}>
              {({ geographies }) => geographies.map((geo) => {
                const countryName = String(geo?.properties?.name || '');
                const normalized = normalizeCountry(countryName);
                const hasData = countryMap.has(normalized) && visibleCountrySet.has(normalized);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => {
                      if (!hasData) return;
                      setHoveredCountry((prev) => (prev === countryName ? prev : countryName));
                    }}
                    onClick={() => {
                      if (!hasData) return;
                      setSelectedCountry((prev) => (prev === countryName ? '' : countryName));
                    }}
                    style={{
                      default: {
                        fill: getCountryColor(countryName),
                        stroke: '#94a3b8',
                        strokeWidth: 0.35,
                        outline: 'none',
                      },
                      hover: {
                        fill: hasData ? '#0f766e' : '#d1d5db',
                        stroke: '#334155',
                        strokeWidth: 0.45,
                        outline: 'none',
                        cursor: hasData ? 'pointer' : 'default',
                      },
                      pressed: {
                        fill: '#115e59',
                        stroke: '#1e293b',
                        strokeWidth: 0.5,
                        outline: 'none',
                      },
                    }}
                  />
                );
              })}
            </Geographies>
          </ComposableMap>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', lg: fullPage ? 460 : 430 },
            p: 2,
            minHeight: fullPage ? 640 : 420,
            height: fullPage ? { xs: 'auto', lg: 'calc(100vh - 210px)' } : { xs: 'auto', lg: 620 },
            overflowY: { xs: 'visible', lg: 'auto' },
          }}
        >
          <Tabs
            value={categoryTab}
            onChange={(_, value) => setCategoryTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 1.5,
              minHeight: 34,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { minHeight: 34, py: 0.5, fontSize: '0.78rem' },
            }}
          >
            {CATEGORY_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {categoryTab === 'global500' ? (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                Top {globalTopByMarketCap.length} by Market Cap
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Ranked across all tracked tickers. Market cap is currently known for {marketCapCoverage.withCap} of {marketCapCoverage.total} tracked tickers.
              </Typography>
              {globalTopByMarketCap.length ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {globalTopByMarketCap.map((tickerRow) => (
                    <Chip
                      key={`global-${tickerRow.ticker}`}
                      label={`${tickerRow.ticker} • ${formatMarketCap(tickerRow.marketCap)}`}
                      onClick={() => handleTickerOpen(tickerRow)}
                      clickable
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.75 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No market cap data available yet.</Typography>
              )}
            </>
          ) : !activePayload ? (
            <Typography variant="body2" color="text.secondary">
              Select a country on the map to see tickers.
            </Typography>
          ) : (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                {activePayload.country}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {activePayload.count} ticker{activePayload.count === 1 ? '' : 's'}
              </Typography>

              {categoryTab === 'all' && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {activePayload.tickers.map((tickerRow) => (
                    <Chip
                      key={`${activePayload.country}-${tickerRow.ticker}`}
                      label={`${tickerRow.ticker} • ${tickerRow.name || tickerRow.ticker}`}
                      onClick={() => handleTickerOpen(tickerRow)}
                      clickable
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.75 }}
                    />
                  ))}
                </Stack>
              )}

              {categoryTab === 'top20' && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {countryTopByMarketCap.length} of {activePayload.count} tickers have known market cap data.
                  </Typography>
                  {countryTopByMarketCap.length ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {countryTopByMarketCap.map((tickerRow) => (
                        <Chip
                          key={`top20-${tickerRow.ticker}`}
                          label={`${tickerRow.ticker} • ${formatMarketCap(tickerRow.marketCap)}`}
                          onClick={() => handleTickerOpen(tickerRow)}
                          clickable
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.75 }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No market cap data available for this country yet.</Typography>
                  )}
                </>
              )}

              {categoryTab === 'sector' && (
                countrySectorGroups.map((group) => (
                  <Box key={group.label} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {group.label} ({group.tickers.length})
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {group.tickers.map((tickerRow) => (
                        <Chip
                          key={`sector-${group.label}-${tickerRow.ticker}`}
                          label={`${tickerRow.ticker} • ${tickerRow.name || tickerRow.ticker}`}
                          onClick={() => handleTickerOpen(tickerRow)}
                          clickable
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.75 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))
              )}
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default CountryTickerMap;

